import { useState } from "react";
import {
  MapPin, Phone, Clock, ExternalLink, Search,
  Navigation, Star, ChevronDown, ChevronUp,
} from "lucide-react";
import Footer from "../components/Footer";

// ── Seed data ─────────────────────────────────────────────────────────────────

const STORES = [
  {
    id: 1,
    name: "Gamapa Downtown",
    address: "12 Market Street, Downtown",
    city: "Lagos",
    phone: "+234 812 000 0001",
    email: "downtown@gamapa.com",
    hours: {
      "Mon–Fri": "08:00 – 22:00",
      "Saturday": "09:00 – 23:00",
      "Sunday":   "10:00 – 21:00",
    },
    rating: 4.8,
    reviews: 312,
    tags: ["Dine-in", "Takeaway", "Delivery"],
    image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=80",
    mapUrl: "https://maps.google.com/?q=Lagos+Nigeria",
    lat: 6.4541,
    lng: 3.3947,
    featured: true,
  },
  {
    id: 2,
    name: "Gamapa Victoria Island",
    address: "45 Adeola Odeku Street, VI",
    city: "Lagos",
    phone: "+234 812 000 0002",
    email: "vi@gamapa.com",
    hours: {
      "Mon–Fri": "09:00 – 22:00",
      "Saturday": "09:00 – 23:00",
      "Sunday":   "11:00 – 20:00",
    },
    rating: 4.6,
    reviews: 188,
    tags: ["Dine-in", "Takeaway", "Catering"],
    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&q=80",
    mapUrl: "https://maps.google.com/?q=Victoria+Island+Lagos",
    lat: 6.4281,
    lng: 3.4219,
    featured: false,
  },
  {
    id: 3,
    name: "Gamapa Ikeja",
    address: "7 Allen Avenue, Ikeja",
    city: "Lagos",
    phone: "+234 812 000 0003",
    email: "ikeja@gamapa.com",
    hours: {
      "Mon–Fri": "08:00 – 21:00",
      "Saturday": "09:00 – 22:00",
      "Sunday":   "Closed",
    },
    rating: 4.5,
    reviews: 240,
    tags: ["Takeaway", "Delivery"],
    image: "https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?w=600&q=80",
    mapUrl: "https://maps.google.com/?q=Ikeja+Lagos",
    lat: 6.6018,
    lng: 3.3515,
    featured: false,
  },
  {
    id: 4,
    name: "Gamapa Lekki",
    address: "22 Admiralty Way, Lekki Phase 1",
    city: "Lagos",
    phone: "+234 812 000 0004",
    email: "lekki@gamapa.com",
    hours: {
      "Mon–Fri": "10:00 – 23:00",
      "Saturday": "10:00 – 00:00",
      "Sunday":   "11:00 – 22:00",
    },
    rating: 4.9,
    reviews: 420,
    tags: ["Dine-in", "Takeaway", "Delivery", "Rooftop"],
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80",
    mapUrl: "https://maps.google.com/?q=Lekki+Phase+1+Lagos",
    lat: 6.4415,
    lng: 3.4786,
    featured: true,
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const isOpenNow = (hours) => {
  const now = new Date();
  const day = now.getDay(); // 0=Sun … 6=Sat
  const key = day === 0 ? "Sunday" : day === 6 ? "Saturday" : "Mon–Fri";
  const slot = hours[key];
  if (!slot || slot === "Closed") return false;
  const [open, close] = slot.split("–").map(s => {
    const [h, m] = s.trim().split(":").map(Number);
    return h * 60 + (m || 0);
  });
  const cur = now.getHours() * 60 + now.getMinutes();
  return cur >= open && cur < close;
};

// ── Sub-components ────────────────────────────────────────────────────────────

const Stars = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(s => (
      <span key={s} className={s <= Math.round(rating) ? "text-amber-400" : "text-gray-200"}>★</span>
    ))}
  </div>
);

const TagPill = ({ label }) => (
  <span className="px-2.5 py-1 bg-orange-50 text-orange-600 text-xs font-semibold rounded-full border border-orange-100">
    {label}
  </span>
);

const HoursDropdown = ({ hours }) => {
  const [open, setOpen] = useState(false);
  const today = new Date().getDay();
  const todayKey = today === 0 ? "Sunday" : today === 6 ? "Saturday" : "Mon–Fri";

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-orange-500 transition"
      >
        <Clock size={14} className="text-gray-400" />
        <span className="font-medium">{hours[todayKey] || "Closed today"}</span>
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>
      {open && (
        <div className="mt-2 bg-gray-50 rounded-xl p-3 space-y-1.5 text-sm">
          {Object.entries(hours).map(([day, slot]) => (
            <div key={day} className="flex justify-between">
              <span className={`text-gray-500 ${day === todayKey ? "font-bold text-gray-800" : ""}`}>{day}</span>
              <span className={`${slot === "Closed" ? "text-red-400" : "text-gray-700"} ${day === todayKey ? "font-bold" : ""}`}>
                {slot}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StoreCard = ({ store }) => {
  const open = isOpenNow(store.hours);
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col">
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={store.image}
          alt={store.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          {store.featured && (
            <span className="px-2.5 py-1 bg-orange-500 text-white text-xs font-bold rounded-full shadow">
              Featured
            </span>
          )}
          <span className={`px-2.5 py-1 text-xs font-bold rounded-full shadow ${open ? "bg-green-500 text-white" : "bg-gray-800/70 text-white"}`}>
            {open ? "Open Now" : "Closed"}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-extrabold text-gray-900">{store.name}</h3>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-3">
          <Stars rating={store.rating} />
          <span className="text-sm font-bold text-gray-700">{store.rating}</span>
          <span className="text-xs text-gray-400">({store.reviews} reviews)</span>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {store.tags.map(t => <TagPill key={t} label={t} />)}
        </div>

        {/* Address */}
        <div className="flex items-start gap-2 text-sm text-gray-600 mb-2">
          <MapPin size={14} className="text-orange-400 flex-shrink-0 mt-0.5" />
          <span>{store.address}, {store.city}</span>
        </div>

        {/* Phone */}
        <a href={`tel:${store.phone}`} className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500 transition mb-3">
          <Phone size={14} className="text-orange-400" />
          {store.phone}
        </a>

        {/* Hours */}
        <HoursDropdown hours={store.hours} />

        {/* Actions */}
        <div className="flex gap-2 mt-auto pt-4">
          <a
            href={store.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold rounded-xl transition"
          >
            <Navigation size={14} /> Get Directions
          </a>
          <a
            href={`mailto:${store.email}`}
            className="px-4 py-2.5 border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600 text-sm font-semibold rounded-xl transition flex items-center gap-1.5"
          >
            <ExternalLink size={13} /> Contact
          </a>
        </div>
      </div>
    </div>
  );
};

// ── Map Panel ─────────────────────────────────────────────────────────────────

const MapPanel = ({ stores, selected, onSelect }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
    {/* Embed a basic OpenStreetMap iframe centred on Lagos */}
    <div className="relative">
      <iframe
        title="Store Locations Map"
        src="https://www.openstreetmap.org/export/embed.html?bbox=3.2%2C6.35%2C3.6%2C6.65&layer=mapnik"
        className="w-full h-64 border-0"
        loading="lazy"
      />
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-white/20 to-transparent" />
    </div>

    {/* Store list in the panel */}
    <div className="p-4 space-y-2">
      {stores.map(s => {
        const open = isOpenNow(s.hours);
        return (
          <button
            key={s.id}
            onClick={() => onSelect(s.id === selected ? null : s.id)}
            className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition ${
              selected === s.id
                ? "bg-orange-50 border border-orange-200"
                : "hover:bg-gray-50 border border-transparent"
            }`}
          >
            <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${open ? "bg-green-500" : "bg-gray-300"}`} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-800 truncate">{s.name}</p>
              <p className="text-xs text-gray-400 truncate">{s.address}</p>
            </div>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${open ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
              {open ? "Open" : "Closed"}
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────

const StoresPage = () => {
  const [query, setQuery] = useState("");
  const [filterTag, setFilterTag] = useState("All");
  const [selectedStore, setSelectedStore] = useState(null);

  const allTags = ["All", ...Array.from(new Set(STORES.flatMap(s => s.tags)))];

  const filtered = STORES.filter(s => {
    const matchQ = !query || s.name.toLowerCase().includes(query.toLowerCase()) || s.address.toLowerCase().includes(query.toLowerCase());
    const matchT = filterTag === "All" || s.tags.includes(filterTag);
    return matchQ && matchT;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white py-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <MapPin size={28} />
          </div>
          <h1 className="text-4xl font-extrabold mb-2">Find a Store Near You</h1>
          <p className="text-orange-100 text-lg mb-8">
            {STORES.length} locations across Lagos — dine in, take away, or order for delivery
          </p>

          {/* Search bar */}
          <div className="relative max-w-md mx-auto">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name or area…"
              className="w-full pl-11 pr-4 py-3.5 rounded-2xl text-gray-800 text-sm font-medium outline-none shadow-lg focus:ring-2 focus:ring-white/50"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Tag filters */}
        <div className="flex items-center gap-2 flex-wrap mb-8">
          {allTags.map(tag => (
            <button
              key={tag}
              onClick={() => setFilterTag(tag)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                filterTag === tag
                  ? "bg-orange-500 text-white shadow-sm"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-orange-300 hover:text-orange-600"
              }`}
            >
              {tag}
            </button>
          ))}
          <span className="text-xs text-gray-400 ml-auto">{filtered.length} location{filtered.length !== 1 ? "s" : ""}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cards */}
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6 content-start">
            {filtered.length === 0 ? (
              <div className="col-span-2 text-center py-20 text-gray-400">
                <MapPin size={40} className="mx-auto mb-3 opacity-30" />
                <p className="font-semibold">No stores match your search.</p>
              </div>
            ) : (
              filtered.map(store => <StoreCard key={store.id} store={store} />)
            )}
          </div>

          {/* Map sidebar */}
          <div className="hidden lg:block sticky top-24 h-fit">
            <MapPanel stores={STORES} selected={selectedStore} onSelect={setSelectedStore} />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default StoresPage;
